import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { COMPANY } from "@/i18n/translations";
import { MapPin, ArrowRight } from "lucide-react";

export default function LocationBanner() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section
      ref={ref}
      className="relative h-[75vh] min-h-[540px] overflow-hidden flex items-center border-y border-white/10"
      data-testid="location-banner"
    >
      <motion.div style={{ y }} className="absolute -top-[10%] left-0 w-full h-[120%]">
        <img
          src="/location/motorworld-facade.webp"
          alt="MotorWorld Köln-Rheinland – Standort von DNA Automotive"
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/70 to-obsidian/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/60" />

      <div className="relative z-10 max-w-[1600px] mx-auto w-full px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          <span
            className="inline-flex items-center gap-2 bg-black/60 backdrop-blur-2xl border border-white/10 text-brand-bright text-xs uppercase tracking-[0.25em] font-bold px-4 py-2 mb-6"
            data-testid="motorworld-badge"
          >
            <MapPin className="w-3.5 h-3.5" />
            {COMPANY.location}
          </span>
          <h2 className="font-heading font-black uppercase tracking-tighter text-white text-4xl md:text-6xl leading-[0.95]">
            {t.locationStrip.title}
          </h2>
          <p className="text-zinc-300 text-base md:text-lg mt-6 leading-relaxed">{t.locationStrip.text}</p>
          <Link
            to="/ueber-uns"
            data-testid="location-banner-cta"
            className="group inline-flex items-center gap-2 mt-8 border border-white/25 text-white font-medium uppercase tracking-wider text-sm px-7 py-4 hover:border-brand-light hover:text-brand-bright transition-colors"
          >
            {t.locationStrip.cta}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
